import moment, { Moment } from 'moment-timezone'
import { useMemo } from 'react'
import { ApolloClient, ApolloError, useQuery } from '@apollo/client'

import { CONFIG_OPTION_EMPTY } from '~/lib/partsConstants'
import {
    DEFAULT_FLASH_TITLE,
    DEFAULT_FLASH_DESCRIPTION,
    PERMISSION_DENIED_TITLE,
    PERMISSION_DENIED_DETAIL,
} from '~/lib/flashMsgs'
import getErrorMessage from '~/lib/getErrorMessage'
import { useFlagEnabled } from '~/lib/isFlagEnabled'

import {
    salesOrder_salesOrder_salesOrderLines_items as SalesOrderLineItemType,
    salesOrder_salesOrder as SalesOrderType,
    salesOrder_salesOrder_salesOrderLines_items_quoteModelRevision_dfmReports as DfmReportType,
} from './OrderDetailsPage/Queries/__generated__/salesOrder'
import { ordersList_me_salesOrders_items_SalesOrder_salesOrderLines_items as UserSalesOrderLineItemType } from './AllOrdersPage/__generated__/ordersList'
import orderDetailsQuery from './OrderDetailsPage/Queries/orderDetailsQuery'
import { dfmFeedbackQuery_salesOrder_salesOrderLines_items_quoteModelRevision_dfmFeedback as DfmFeedbackType } from './OrderDetailsPage/Status/__generated__/dfmFeedbackQuery'
import { salesOrderLines_salesOrderLines_items_quoteModelRevision_quoteModel as QuoteModelType } from './OrderDetailsPage/Queries/__generated__/salesOrderLines'

export const SALES_LINES_PAGINATION_LIMIT = 2
export const SALES_ORDER_LINES_PAGINATION_LIMIT = 12
export const MOLDS_PAGINATION_LIMIT = 2
export const SHIPMENT_LINES_PAGINATION_LIMIT = 12
export const PAGINATION_LIMIT = 5
export const DEFAULT_PAGE_NUMBER = 1

const HTS_RELEASE_DATE = '2020-02-13'
const PLATFORM_USER_ID = 'fictivUserId'
const IS_PUBLISHED = 'isPublished'

export const FICTIV_SHIPPING_UPGRADE_PROGRAM_LINK =
    ' https://help.fictiv.com/en/articles/4385397-fictiv-shipment-upgrade-program-for-international-orders'

export type ApolloClientType = ApolloClient<object>
interface OrderDetailDataType {
    data?: {
        salesOrder: SalesOrderType
    }
    refetch: Function
    loading: boolean
    error?: ApolloError
}

export const ORDER_PAGE_FLAGS = Object.freeze({
    REORDER: 'FLAG_REORDER',
    INVALID_CONFIG_REORDER: 'INVALID_CONFIG_REORDER',
    RELEASE_TRANSPARENCY_IMPROVEMENT: 'RELEASE_TRANSPARENCY_IMPROVEMENT',
    UI_CPQ_QUOTES_INTEGRATION_V2: 'UI_CPQ_QUOTES_INTEGRATION_V2',
})

export const useOrderDetailData = (orderName: string): OrderDetailDataType => {
    const isCpqFlagV2Enabled = useFlagEnabled(
        ORDER_PAGE_FLAGS.UI_CPQ_QUOTES_INTEGRATION_V2,
    )

    const variables: {
        name: string
        isCpqFlagV2Enabled: boolean
    } = {
        name: orderName,
        isCpqFlagV2Enabled,
    }

    return useQuery(orderDetailsQuery, {
        variables: variables,
        skip: !orderName,
    })
}

export function formatDate(date: string | null | Date) {
    return date ? moment(date).format('dddd, MM-DD-YYYY') : CONFIG_OPTION_EMPTY
}

export function getHtsDueDate(date: string | Date) {
    return moment(date).add(2, 'days')
}

export function htsDeadlinePassed(date: Moment) {
    return moment().isAfter(date)
}

type PartialSalesOrderLineItemType = Pick<
    SalesOrderLineItemType,
    'htsCodeSubmitted'
>
type PartialUserSalesOrderLineItemType = Pick<
    UserSalesOrderLineItemType,
    'htsCodeSubmitted'
>

type OrderLineType =
    | PartialSalesOrderLineItemType
    | PartialUserSalesOrderLineItemType

type OrderLinesType =
    | PartialSalesOrderLineItemType[]
    | PartialUserSalesOrderLineItemType[]
    | []

export interface QuoteModelRevisionType {
    id: string
    quoteModel?: QuoteModelType
    dfmFeedback?: DfmFeedbackType[]
    dfmReports?: DfmReportType[]
}

interface SalesOrderLineType {
    id: string
    quoteModelRevision: QuoteModelRevisionType
}

export const isAllHtsCodesSubmitted = (
    salesOrderLineItems: OrderLinesType = [],
    isIm = false,
): boolean =>
    isIm
        ? salesOrderLineItems.some(
              (salesOrderLineItem: OrderLineType) =>
                  salesOrderLineItem.htsCodeSubmitted,
          )
        : salesOrderLineItems.every(
              (salesOrderLineItem: OrderLineType) =>
                  salesOrderLineItem.htsCodeSubmitted,
          )

export const shipDateBeforeHtsDate = (expectedShipDate: Moment | string) => {
    return moment(expectedShipDate).isBefore(moment(HTS_RELEASE_DATE))
}

export function formatDateOrder(date: string | Date) {
    return date ? moment(date).format('MMMM DD, YYYY') : CONFIG_OPTION_EMPTY
}

export function formatDateAllOrders(date: string | null | Date) {
    if (!date) {
        return CONFIG_OPTION_EMPTY
    }

    return moment(date).format('dddd, MMMM DD, YYYY')
}

export const getErrorMessageAndDescription = (
    error?: ApolloError,
): [boolean, string, string[]] => {
    const isPermissionDeniedError =
        getErrorMessage(error).includes(PLATFORM_USER_ID)
    const errorMessage =
        (isPermissionDeniedError && PERMISSION_DENIED_TITLE) ||
        DEFAULT_FLASH_TITLE

    const description =
        (isPermissionDeniedError && PERMISSION_DENIED_DETAIL) ||
        DEFAULT_FLASH_DESCRIPTION

    const [descPrefix, descSuffix] = description.split('please')

    return [
        isPermissionDeniedError,
        errorMessage,
        [`${descPrefix} please `, descSuffix],
    ]
}

export const useAllPartsReportsReleased = (
    salesOrderLinesItems: SalesOrderLineType[],
    hasDfmReport: boolean,
) => {
    return useMemo(() => {
        const publishedFlag = salesOrderLinesItems?.some(saleOrderLinesItem => {
            if (hasDfmReport) {
                const dfmReport =
                    saleOrderLinesItem?.quoteModelRevision?.dfmReports?.filter(
                        report => report[IS_PUBLISHED] === false,
                    )

                return dfmReport?.length
            }
            const dfmFeedback =
                saleOrderLinesItem?.quoteModelRevision?.dfmFeedback?.filter(
                    feedback => feedback[IS_PUBLISHED] === false,
                )

            return dfmFeedback?.length
        })

        return publishedFlag
    }, [salesOrderLinesItems, hasDfmReport])
}
